import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { operatorConsole, PoolOfferConfig, type RtpSkippedPack } from '../../lib/api/operatorConsole';
import { PercentField } from './PercentField';
import { CommonValue, formatMoney, MAX_LOOT_RTP } from './pricing';
import { SkippedPacksNotice } from './SkippedPacksNotice';

const LEVELS_NOTE = 'High and Max keep their own RTP where one is set.';
const FIXED_RATES_NOTE = 'Fixed-rate packs get this RTP on every level.';

type Props = {
  /** Gated on the operator's allow_pricing_overrides flag by the parent. */
  allowPricing: boolean;
  /** RTP % / Buyback % most packs share, and how many packs differ. */
  rtp: CommonValue;
  buyback: CommonValue;
  /** A real pack price for the "$X pack holds $Y" explanation. */
  examplePrice: number;
  /** Packs ticked in the table; empty means every pack. */
  selectedIds: number[];
  /** Some target pack is on fixed rates: the backend sets the RTP of all its levels, its win rates stay. */
  fixedRates: boolean;
  /** Receives the refreshed config list the backend returns on success. */
  onApplied: (configs: PoolOfferConfig[]) => void;
};

function packsLabel(n: number): string {
  return n === 1 ? '1 selected pack' : `${n} selected packs`;
}

function asInput(value: number | null): string {
  return value != null ? String(value) : '';
}

function differingNote(n: number): string | null {
  if (n === 0) return null;
  return n === 1 ? '1 pack uses its own value' : `${n} packs use their own value`;
}

export function GlobalPricingBar({
  allowPricing,
  rtp,
  buyback,
  examplePrice,
  selectedIds,
  fixedRates,
  onApplied,
}: Props) {
  const [rtpDraft, setRtpDraft] = useState(asInput(rtp.value));
  const [buybackDraft, setBuybackDraft] = useState(asInput(buyback.value));
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<RtpSkippedPack[]>([]);

  // Inputs always show the saved values, re-synced when packs change.
  useEffect(() => setRtpDraft(asInput(rtp.value)), [rtp.value]);
  useEffect(() => setBuybackDraft(asInput(buyback.value)), [buyback.value]);

  if (!allowPricing) return null;

  const rtpTrimmed = rtpDraft.trim();
  const buybackTrimmed = buybackDraft.trim();
  // A field is sent when it was edited, or when some packs differ from it —
  // Apply then brings them back in line.
  const rtpPending = rtpTrimmed !== '' && (rtpTrimmed !== asInput(rtp.value) || rtp.differing > 0);
  const buybackPending =
    buybackTrimmed !== '' && (buybackTrimmed !== asInput(buyback.value) || buyback.differing > 0);
  const nothingToApply = !rtpPending && !buybackPending;
  const target = selectedIds.length > 0 ? packsLabel(selectedIds.length) : 'all packs';
  const levelsNote = fixedRates ? `${LEVELS_NOTE} ${FIXED_RATES_NOTE}` : LEVELS_NOTE;

  const rtpNumber = Number(rtpTrimmed);
  const buybackNumber = Number(buybackTrimmed);
  const rtpExample =
    rtpTrimmed !== '' && Number.isFinite(rtpNumber)
      ? formatMoney(Math.round(examplePrice * rtpNumber) / 100)
      : null;

  async function handleApply() {
    setError(null);
    setSkipped([]);

    const payload: { rtp?: number; buyback?: number } = {};
    if (rtpPending) {
      if (!Number.isFinite(rtpNumber) || rtpNumber <= 0) {
        setError('RTP must be a number greater than 0.');
        return;
      }
      if (rtpNumber > MAX_LOOT_RTP) {
        setError(`RTP can't be above ${MAX_LOOT_RTP}%.`);
        return;
      }
      payload.rtp = rtpNumber;
    }
    if (buybackPending) {
      if (!Number.isFinite(buybackNumber) || buybackNumber < 0 || buybackNumber > 100) {
        setError('Buyback must be a number between 0 and 100.');
        return;
      }
      payload.buyback = buybackNumber;
    }
    if (payload.rtp === undefined && payload.buyback === undefined) return;

    if (
      !window.confirm(
        `This sets the Normal RTP % and Buyback % on ${target}, replacing custom values. ${levelsNote} Continue?`,
      )
    )
      return;

    setApplying(true);
    try {
      // Same filters the grid was loaded with (see ConfigsPage.loadConfigs):
      // the backend prices exactly the packs it lists back, so an unfiltered
      // call would both overspray pricing onto packs this operator never got
      // and replace the grid with that superset.
      const { pool_offer_configs, rtp_skipped } = await operatorConsole.applyGlobalPricing({
        ...payload,
        availableOnly: true,
        includeStaged: true,
        ...(selectedIds.length > 0 ? { poolOfferIds: selectedIds } : {}),
      });
      onApplied(pool_offer_configs);
      setSkipped(rtp_skipped ?? []);
    } catch (err) {
      // ApiError carries the backend `message` (403 pricing_overrides_disabled /
      // 422 invalid_pricing). Network failures fall through to their own message.
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to apply global pricing.');
      }
    } finally {
      setApplying(false);
    }
  }

  return (
    <div className="mb-4 rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-[14px] font-semibold text-[var(--co-text)]">Defaults for {target}</h2>
        {error && (
          <span className="rounded bg-[var(--co-error)] px-2 py-1 text-[11px] font-medium text-white">
            {error}
          </span>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <PercentField
          id="global-rtp"
          label="RTP (Normal)"
          value={rtpDraft}
          disabled={applying}
          onChange={(v) => {
            setRtpDraft(v);
            setError(null);
            setSkipped([]);
          }}
          note={differingNote(rtp.differing)}
          help={
            rtpExample
              ? `Share of the pack price returned to players as cards. At ${rtpTrimmed}%, a ${formatMoney(examplePrice)} pack holds about ${rtpExample} of cards on average. ${levelsNote}`
              : `Share of the pack price returned to players as cards. ${levelsNote}`
          }
        />
        <PercentField
          id="global-buyback"
          label="Buyback"
          value={buybackDraft}
          max={100}
          disabled={applying}
          onChange={(v) => {
            setBuybackDraft(v);
            setError(null);
          }}
          note={differingNote(buyback.differing)}
          help={
            buybackTrimmed !== ''
              ? `Players can instantly sell a pulled card back for ${buybackTrimmed}% of its market value.`
              : 'What players get when they instantly sell a pulled card back.'
          }
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void handleApply()}
          disabled={nothingToApply || applying}
          className="rounded bg-[var(--co-brand)] px-3 py-1.5 text-[12px] font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:bg-transparent disabled:text-[var(--co-muted)] disabled:ring-1 disabled:ring-[var(--co-border)]"
        >
          {applying ? 'Applying…' : `Apply to ${target}`}
        </button>
        <span className="text-[11px] text-[var(--co-muted)]">
          {nothingToApply
            ? `${selectedIds.length > 0 ? 'Selected packs' : 'All packs'} already use these values.`
            : selectedIds.length > 0
              ? 'Only the ticked packs change. Clear the selection to apply to every pack.'
              : 'Replaces RTP and Buyback on every pack. Tick packs below to change only some of them.'}
        </span>
      </div>

      <SkippedPacksNotice packs={skipped} />
    </div>
  );
}
